import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getWorkers, createWorker, updateWorker, deleteWorker, getWorkCenters } from '../services/api';
import { Worker, WorkCenter } from '../types';

const Workers: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [workCenters, setWorkCenters] = useState<WorkCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingWorker, setEditingWorker] = useState<Worker | null>(null);
  const [newWorker, setNewWorker] = useState<{
    username: string;
    email: string;
    name: string;
    surname: string;
    work_center_id: string;
  }>({
    username: '',
    email: '',
    name: '',
    surname: '',
    work_center_id: ''
  });

  const handleEdit = (worker: Worker) => {
    setEditingWorker(worker);
    setNewWorker({
      username: worker.username,
      email: worker.email ?? '',  // Convert null to empty string for form
      name: worker.name,
      surname: worker.surname ?? '',  // Convert null to empty string for form
      work_center_id: worker.work_center_id.toString()
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar este trabajador?')) {
      try {
        await deleteWorker(id);
        // Refresh workers list
        const updatedWorkers = await getWorkers();
        setWorkers(updatedWorkers);
      } catch (error) {
        console.error('Error deleting worker:', error);
        alert('Error al eliminar el trabajador');
      }
    }
  };

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70,
      flex: isMobile ? 0 : undefined
    },
    { 
      field: 'name', 
      headerName: 'Nombre', 
      flex: isMobile ? 1 : undefined,
      width: isMobile ? undefined : 130,
      minWidth: isMobile ? 80 : 130
    },
    { 
      field: 'surname', 
      headerName: 'Apellidos', 
      flex: isMobile ? 0 : undefined,
    },
    { 
      field: 'username', 
      headerName: 'Usuario',
      flex: isMobile ? 0 : undefined,
      width: isMobile ? undefined : 120,
      minWidth: isMobile ? 60 : 120
    },
    { 
      field: 'email', 
      headerName: 'Email', 
      width: 200,
      flex: isMobile ? 0 : undefined
    },
    {
      field: 'work_center_id',
      headerName: 'Centro',
      flex: isMobile ? 1.2 : undefined,
      width: isMobile ? undefined : 200,
      minWidth: isMobile ? 120 : 200,
      renderCell: (params) => {
        const workCenter = workCenters.find(wc => wc.id === params.value);
        return workCenter ? workCenter.name : 'N/A';
      }
    },
    {
      field: 'is_active',
      headerName: 'Estado',
      width: 120,
      flex: isMobile ? 0 : undefined,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          sx={{
            backgroundColor: params.value ? '#f8f9fa' : '#f0f0f0',
            color: params.value ? '#1a1a1a' : '#666666',
            border: `1px solid ${params.value ? '#e9ecef' : '#e0e0e0'}`,
            borderRadius: 0,
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 500
          }}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: isMobile ? 90 : 120,
      flex: isMobile ? 0 : undefined,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon sx={{ fontSize: 18, color: '#666666' }} />}
          label="Editar"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon sx={{ fontSize: 18, color: '#666666' }} />}
          label="Eliminar"
          onClick={() => handleDelete(params.row.id)}
        />
      ]
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [workersData, workCentersData] = await Promise.all([
          getWorkers(),
          getWorkCenters()
        ]);
        setWorkers(workersData);
        setWorkCenters(workCentersData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCreateOrUpdateWorker = async () => {
    try {
      const workerData = {
        ...newWorker,
        work_center_id: Number(newWorker.work_center_id),
        // Convert empty strings to null for optional fields
        email: newWorker.email.trim() === '' ? null : newWorker.email,
        surname: newWorker.surname.trim() === '' ? null : newWorker.surname
      };

      if (editingWorker) {
        const updatedWorker = await updateWorker(editingWorker.id, workerData);
        setWorkers(workers.map(w => w.id === editingWorker.id ? updatedWorker : w));
      } else {
        const createdWorker = await createWorker(workerData);
        setWorkers([...workers, createdWorker]);
      }

      handleCloseDialog();
    } catch (error) {
      console.error('Error saving worker:', error);
      alert('Error al guardar el trabajador');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingWorker(null);
    setNewWorker({ username: '', email: '', name: '', surname: '', work_center_id: '' });
  };

  return (
    <Box sx={{ p: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'start' }} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }} mb={{ xs: 3, sm: 6 }}>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 1,
              fontWeight: 700,
              color: '#001923',
              letterSpacing: '-0.01em',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Trabajadores
          </Typography>
          <Typography 
            variant="body1" 
            className="descripcion-cyan"
            sx={{ 
              fontWeight: 300,
              lineHeight: 1.6
            }}
          >
            Gestión de empleados y centros
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{
            backgroundColor: '#004254',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#003040'
            },
            alignSelf: { xs: 'stretch', sm: 'auto' }
          }}
        >
          Nuevo Trabajador
        </Button>
      </Box>

      <Paper 
        className="workers-datagrid-paper"
        sx={{ 
          height: { xs: 'calc(100vh - 280px)', sm: 600 },
          minHeight: 400, 
          width: '100%',
          backgroundColor: '#FFFFFF',
          '&.workers-datagrid-paper .MuiDataGrid-root': {
            border: 'none',
            backgroundColor: '#FFFFFF'
          },
          '&.workers-datagrid-paper .MuiDataGrid-columnHeaders': {
            backgroundColor: '#00B0BD',
            borderBottom: '2px solid #00B0BD',
            color: '#001923'
          },
          '&.workers-datagrid-paper .MuiDataGrid-columnHeaderTitle': {
            color: '#001923',
            fontWeight: 600
          },
          '&.workers-datagrid-paper .MuiDataGrid-columnHeader': {
            padding: { xs: '0 8px', sm: '0 16px' }
          },
          '&.workers-datagrid-paper .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
            display: { xs: 'none !important', sm: 'inline-flex' }
          },
          '&.workers-datagrid-paper .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
            display: { xs: 'none !important', sm: 'inline-flex' }
          },
          '&.workers-datagrid-paper .MuiDataGrid-cell': {
            borderBottom: '1px solid #f0f0f0',
            color: '#001923'
          },
          '@media (max-width: 600px)': {
            '&.workers-datagrid-paper .MuiDataGrid-actionsCell': {
              gap: '0px'
            },
            '&.workers-datagrid-paper .MuiDataGrid-actionsCell .MuiButtonBase-root': {
              padding: '4px',
              minWidth: 'unset'
            }
          },
          '&.workers-datagrid-paper .MuiDataGrid-row': {
            backgroundColor: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }
        }}
      >
        <DataGrid
          rows={workers}
          columns={columns}
          columnVisibilityModel={{
            id: !isMobile,
            surname: !isMobile,
            email: !isMobile,
            is_active: !isMobile
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 }
            }
          }}
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnMenu={isMobile}
        />
      </Paper>

      <Dialog
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: '1px solid #f0f0f0'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.1rem',
            fontWeight: 400,
            color: '#1a1a1a',
            borderBottom: '1px solid #f0f0f0',
            pb: 2
          }}
        >
          {editingWorker ? 'Editar Trabajador' : 'Nuevo Trabajador'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Nombre"
              value={newWorker.name}
              onChange={(e) => setNewWorker({ ...newWorker, name: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  fontWeight: 300
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 300,
                  color: '#666666'
                }
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Apellidos (opcional)"
              value={newWorker.surname}
              onChange={(e) => setNewWorker({ ...newWorker, surname: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  fontWeight: 300
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 300,
                  color: '#666666'
                }
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Usuario"
              value={newWorker.username}
              onChange={(e) => setNewWorker({ ...newWorker, username: e.target.value })}
              disabled={!!editingWorker}
              helperText={editingWorker ? "El nombre de usuario no se puede modificar" : "Nombre de usuario para iniciar sesión"}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  fontWeight: 300,
                  backgroundColor: editingWorker ? '#f5f5f5' : 'transparent'
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 300,
                  color: '#666666'
                },
                '& .MuiFormHelperText-root': {
                  fontWeight: 300,
                  fontSize: '0.75rem'
                }
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Email (opcional)"
              type="email"
              value={newWorker.email}
              onChange={(e) => setNewWorker({ ...newWorker, email: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  fontWeight: 300
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 300,
                  color: '#666666'
                }
              }}
            />
            <FormControl 
              fullWidth 
              margin="normal"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  fontWeight: 300
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 300,
                  color: '#666666'
                }
              }}
            >
              <InputLabel>Centro de Trabajo</InputLabel>
              <Select
                value={newWorker.work_center_id}
                label="Centro de Trabajo"
                onChange={(e) => setNewWorker({ ...newWorker, work_center_id: e.target.value as string })}
              >
                {workCenters.map((center) => (
                  <MenuItem 
                    key={center.id} 
                    value={center.id}
                    sx={{
                      fontWeight: 300,
                      py: 1.5
                    }}
                  >
                    {center.name} — {center.location}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #f0f0f0' }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              backgroundColor: '#FFFFFF',
              color: '#004254',
              borderColor: '#004254',
              textTransform: 'none',
              fontFamily: 'ForFutureSans, sans-serif',
              fontWeight: 700,
              borderRadius: 0,
              px: 3,
              '&:hover': {
                backgroundColor: '#f0f7f9',
                borderColor: '#004254',
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleCreateOrUpdateWorker}
            variant="contained"
            sx={{
              backgroundColor: '#004254',
              color: '#FFFFFF',
              textTransform: 'none',
              fontFamily: 'ForFutureSans, sans-serif',
              fontWeight: 700,
              borderRadius: 0,
              px: 3,
              '&:hover': {
                backgroundColor: '#003040',
              }
            }}
          >
            {editingWorker ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Workers;